// Map (`/map`, WEB_PLAN.md §5.7, Phase 1 = read): a port of GroundTeam/Views/MapTab/MapCanvasView.swift and
// MapTab+Store.swift. A full-height MapLibre map of the filtered visible stores that have a valid coordinate, pins
// colored by the "Color by" rule (default `settings.defaultMapColorize`) with the iOS legend (counts of the pins in view), the shared
// store filter (`MapFilter`, kept in the URL query like the Stores list) in a side panel next to a keyboard-friendly
// list of the same stores, territory and rep-position layers, and a peek card for the selected pin.
//
// The filter and color override live in the URL so a view can be shared and the Stores list can open the same set;
// the last view is also remembered for the browser session (iOS keeps it in MapSession.shared), so leaving the map and
// coming back from the rail keeps your filters. Layer toggles are remembered the same way.
//
// Managers also get Select (`canSelectOnMap`): in select mode a pin click toggles the store, Shift-drag or the box
// tool selects every filtered store inside a rectangle (clustered ones too), "Select all N" takes every store the
// filters let through, the Stores tab lists them with checkboxes, and the bulk bar assigns them all to one owner
// (`canAssign`, AssignDialog). The selection is a set of ids held here; the map draws it as halos, so picking stores
// re-renders no pin. Admins and managers can also import stores from a CSV file here (`canCreateRecords`: the Import
// button, and the empty map), the same dialog as Stores › Import.

import { Layers, ListFilter, Maximize, Rows3, SlidersHorizontal, SquareMousePointer, Upload } from 'lucide-react';
import type { MapMouseEvent } from 'maplibre-gl';
import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import type { MapColorize } from '@/contracts/enums';
import type { RecordPayload } from '@/contracts/models';
import {
  usePipelines,
  useRecordLookups,
  useRepPositions,
  useSettings,
  useTerritories,
  useViewer,
  useVisibleRecords,
  useVisibleUsers,
  useVisitResults,
  type DataItem,
} from '@/data/hooks';
import { formatGrouped } from '@/domain/format';
import { isValidCoordinate } from '@/domain/geo';
import {
  EMPTY_FILTER,
  filterActiveCount,
  filterToQuery,
  type FilterContext,
  type MapFilter,
  type RecordLookups,
} from '@/domain/records';
import { pickerUsers } from '@/domain/team';
import { compareLocalized, idKey, idSet } from '@/domain/util';
import { useSession } from '@/auth/AuthProvider';
import {
  FirstSyncProgress,
  MapCanvas,
  NoStoresYet,
  useDocumentTitle,
  useFirstSync,
  useStoreRowLookups,
  type MapBounds,
  type MapCanvasHandle,
  type MapLibreMap,
} from '@/features/common';
import { ImportStoresDialog } from '@/features/stores/import/ImportStoresDialog';
import { NO_STORES_IMPORT_TEXT } from '@/features/stores/import/importModel';
import { storesByTerritory, visibleTerritories } from '@/features/territories/territoryModel';
import { useCan } from '@/session/permissions';
import { Button, DropdownMenu, IconButton, PRESENCE_COLORS, Tabs, cn, isModalOpen, useMediaQuery, useTicker, useToast } from '@/ui';
import { AssignDialog } from './AssignDialog';
import { ColorByMenu } from './ColorByMenu';
import { MapFilterPanel, type TerritoryOption } from './MapFilterPanel';
import { MapLegend } from './MapLegend';
import { MapSelectionBar } from './MapSelectionBar';
import { MapStoreList } from './MapStoreList';
import { StorePeek } from './StorePeek';
import {
  EMPTY_SELECTION,
  addToSelection,
  allShown,
  assignedText,
  escapeLeavesSelectMode,
  listPickPanTarget,
  selectedStores,
  storesInBox,
  toggleSelected,
  type Selection,
} from './mapSelection';
import { loadLayers, loadSavedView, saveLayers, saveView, type MapLayerToggles } from './mapSession';
import {
  createViewportStore,
  filterMapStores,
  hasMapState,
  legendEmptyText,
  mapLegend,
  mapQuery,
  peekOnMap,
  readMapQuery,
  repMarkers,
  restorableQuery,
  storePins,
  storesInView,
  tagOptions,
  territoryPolygons,
  type MapUrlState,
  type ViewportStore,
} from './mapViewModel';

/** Side panel inline from this width; narrower screens get it as an overlay drawer. */
const WIDE = '(min-width: 1024px)';

export default function MapPage() {
  useDocumentTitle('Map');
  const sync = useFirstSync();
  const { companyId } = useSession();
  const settings = useSettings();
  const [params] = useSearchParams();
  const mounted = useRef(false);

  if (!sync.ready) {
    return (
      <div className="p-6">
        <FirstSyncProgress what="Map" />
      </div>
    );
  }
  // Coming back from the rail (no query) restores this session's last view; a shared link keeps its own. On the
  // first visit the map waits for the restored query (so its first fit frames the restored set); once it is up, it
  // stays mounted while the query is put back (a rail click while on the map must not rebuild the map). Only a
  // normalized query that reads back as map state is restored, so the redirect cannot repeat forever.
  const saved = restorableQuery(loadSavedView(companyId), params, settings.defaultMapColorize);
  const restore = saved !== null ? <Navigate to={{ search: `?${saved}` }} replace /> : null;
  if (restore && !mounted.current) return restore;
  mounted.current = true;
  return (
    <>
      {restore}
      <MapScreen />
    </>
  );
}

type PanelTab = 'filters' | 'stores';

function MapScreen() {
  const { companyId } = useSession();
  const [params, setParams] = useSearchParams();
  const settings = useSettings();
  const viewer = useViewer();
  const can = useCan();
  const isWide = useMediaQuery(WIDE);
  const mapRef = useRef<MapCanvasHandle>(null);
  const panelId = useId();

  // MARK: View state (URL)

  const query = params.toString();
  const view = useMemo(() => readMapQuery(new URLSearchParams(query)), [query]);
  const filter = view.filter;
  const mode: MapColorize = view.colorize ?? settings.defaultMapColorize;

  const updateView = useCallback(
    (next: MapUrlState) => {
      const q = mapQuery(next, settings.defaultMapColorize);
      saveView(companyId, q.toString());
      setParams(q, { replace: true });
    },
    [companyId, setParams, settings.defaultMapColorize],
  );
  const setFilter = useCallback((f: MapFilter) => updateView({ filter: f, colorize: view.colorize }), [updateView, view.colorize]);
  const setMode = useCallback((m: MapColorize) => updateView({ filter, colorize: m }), [updateView, filter]);
  const resetFilter = useCallback(() => updateView({ filter: { ...EMPTY_FILTER }, colorize: view.colorize }), [updateView, view.colorize]);

  // A view opened from a shared link becomes this tab's remembered view too (edits already save themselves), so
  // leaving and coming back from the rail returns to it rather than to an older one.
  useEffect(() => {
    if (hasMapState(new URLSearchParams(query))) saveView(companyId, mapQuery(view, settings.defaultMapColorize).toString());
  }, [companyId, query, view, settings.defaultMapColorize]);

  const [panelOpen, setPanelOpen] = useState<boolean>(() => window.matchMedia?.(WIDE).matches ?? true);
  const filtersButton = useRef<HTMLButtonElement>(null);
  const [tab, setTab] = useState<PanelTab>('filters');
  const [layers, setLayersState] = useState<MapLayerToggles>(loadLayers);
  const setLayers = (next: MapLayerToggles) => {
    setLayersState(next);
    saveLayers(next);
  };
  const [peekId, setPeekId] = useState<string | null>(null);
  const closePeek = useCallback(() => setPeekId(null), []);

  // MARK: Selection (managers)

  const [selectMode, setSelectMode] = useState(false);
  const selecting = can.canSelectOnMap && selectMode;
  const [boxTool, setBoxTool] = useState(false);
  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);
  const [assignOpen, setAssignOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const boxToolButton = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const clearSelection = useCallback(() => setSelection(EMPTY_SELECTION), []);
  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectMode(false);
      setBoxTool(false);
      setSelection(EMPTY_SELECTION);
    } else {
      setSelectMode(true);
      setPeekId(null);
    }
  };

  // MARK: Map events (through onMapReady: MapCanvas has no viewport or empty-click callbacks)

  const viewport = useMemo(createViewportStore, []);
  // Set by a pin click; the map-wide click that follows it in the same event then keeps the peek open.
  const pinClicked = useRef(false);
  const onPointClick = useCallback(
    (id: string) => {
      pinClicked.current = true;
      if (selecting) setSelection((s) => toggleSelected(s, id));
      else setPeekId(id);
    },
    [selecting],
  );
  const onMapReady = useCallback(
    (map: MapLibreMap) => {
      const report = () => {
        const b = map.getBounds();
        viewport.set([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
      };
      map.on('moveend', report);
      report();
      // iOS deselects on a tap on the bare map. A cluster click zooms in and keeps the peek, like tapping a cluster
      // on iOS. Layer handlers (the pin's) run in the same dispatch, so the decision waits one task for their flag.
      map.on('click', (e: MapMouseEvent) => {
        let onCluster = false;
        try {
          onCluster = map.queryRenderedFeatures(e.point).some((f) => f.properties?.cluster === true);
        } catch {
          // Style switching (light/dark): nothing is queryable for a moment; treat it as the bare map.
        }
        window.setTimeout(() => {
          const keep = pinClicked.current || onCluster;
          pinClicked.current = false;
          if (!keep) setPeekId(null);
        }, 0);
      });
    },
    [viewport],
  );

  // MARK: Data

  const visible = useVisibleRecords();
  const lk = useRecordLookups();
  const pipelines = usePipelines();
  const visitResults = useVisitResults();
  const territories = useTerritories();
  const visibleUsers = useVisibleUsers();
  const positions = useRepPositions();
  const minute = useTicker(60_000);

  const ctx = useMemo<FilterContext>(
    () => ({ stats: lk.stats, pipelines: pipelines.values, viewerId: viewer?.profileId ?? null, now: lk.now }),
    [lk, pipelines.values, viewer?.profileId],
  );
  // Filtering is the expensive step; recoloring (mode) and the legend reuse its result.
  const stores = useMemo(() => filterMapStores(visible.values, filter, ctx), [visible.values, filter, ctx]);
  const points = useMemo(() => storePins(stores.located, mode, lk), [stores.located, mode, lk]);
  const pinColors = useMemo(() => new Map(points.map((p) => [idKey(p.id), p.color] as const)), [points]);
  const locatedIds = useMemo(() => idSet(stores.located.map((r) => r.id)), [stores.located]);

  const polygons = useMemo(() => territoryPolygons(territories.values), [territories.values]);
  const markers = useMemo(() => {
    void minute; // presence colors age every minute
    return can.canSeeRepLocations ? repMarkers(positions, visibleUsers.values, new Date(), PRESENCE_COLORS) : [];
  }, [can.canSeeRepLocations, positions, visibleUsers.values, minute]);

  // Filter panel options (iOS MapFilterSheet sources).
  const owners = useMemo(() => pickerUsers(visibleUsers.values), [visibleUsers.values]);
  const orderedResults = useMemo(
    () => [...visitResults.values].sort((a, b) => a.order - b.order || compareLocalized(a.name, b.name)),
    [visitResults.values],
  );
  const territoryOptions = useMemo<TerritoryOption[]>(() => {
    const list = visibleTerritories(
      territories.values,
      { profileId: viewer?.profileId, role: viewer?.role },
      settings.allowRepsToCreateTerritories,
    );
    const members = storesByTerritory(list, visible.values);
    return list.map((t) => ({ territory: t, storeCount: members.get(idKey(t.id))?.length ?? 0 }));
  }, [territories.values, viewer?.profileId, viewer?.role, settings.allowRepsToCreateTerritories, visible.values]);
  const tags = useMemo(() => tagOptions(visible.values, filter.tags), [visible.values, filter.tags]);

  // The selection as it applies now: only stores still on the map (a filter change hides the rest, never assigns them).
  const selected = useMemo(() => selectedStores(stores.located, selection), [stores.located, selection]);
  const onBoxSelect = useCallback(
    (box: MapBounds) => setSelection((s) => addToSelection(s, storesInBox(stores.located, box).map((r) => r.id))),
    [stores.located],
  );
  const selectAllShown = useCallback(() => setSelection(allShown(stores.located)), [stores.located]);
  const onAssigned = (count: number, ownerName: string | null) => {
    setAssignOpen(false);
    setSelection(EMPTY_SELECTION);
    if (count > 0) toast.success(assignedText(count, ownerName));
    else toast.info('No stores were changed: they were deleted on another device.');
    // The dialog gives focus back to "Assign to…", which the cleared selection removes: keep the keyboard in the
    // selection bar (the box tool is always there) instead of dropping it to the top of the page.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (!active || active === document.body || !active.isConnected) boxToolButton.current?.focus({ preventScroll: true });
    });
  };

  // Escape clears the selection, then leaves select mode (`escapeLeavesSelectMode`). Dialogs, menus, the narrow drawer,
  // the Stores list and search fields handle their own Escape first (they prevent it), and typing in a field is never
  // interrupted.
  const selectedCount = selected.length;
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || isModalOpen()) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      setSelection(EMPTY_SELECTION);
      if (escapeLeavesSelectMode(selectedCount)) {
        setSelectMode(false);
        setBoxTool(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selecting, selectedCount]);

  const activeFilters = filterActiveCount(filter);
  const listHref = `/stores${filterQueryString(filter)}`;
  const shownPeekId = selecting ? null : peekOnMap(peekId, locatedIds);
  const peekItem = shownPeekId ? visible.get(shownPeekId) : undefined;

  const selectStore = useCallback(
    (id: string) => {
      const r = visible.get(id)?.value;
      if (selecting) {
        // In select mode a row toggles the store like its pin. The drawer stays open and the zoom stays put, so many
        // stores can be picked from the list in a row; the map only pans when the store is out of view.
        setSelection((s) => toggleSelected(s, id));
        const target = listPickPanTarget(r, viewport.get());
        if (target) mapRef.current?.getMap()?.easeTo({ center: [target.longitude, target.latitude] });
        return;
      }
      setPeekId(id);
      if (r && isValidCoordinate(r.coordinate)) mapRef.current?.flyTo(r.coordinate, 16);
      if (!isWide) setPanelOpen(false);
    },
    [visible, isWide, selecting, viewport],
  );
  const onListSelectionChange = useCallback((ids: Set<string>) => setSelection(ids), []);

  const summary =
    stores.filterActive
      ? `${formatGrouped(stores.matching.length)} of ${formatGrouped(stores.totalVisible)} stores match your filters`
      : `${formatGrouped(stores.totalVisible)} ${stores.totalVisible === 1 ? 'store' : 'stores'}`;

  return (
    <div className="flex h-full min-h-[480px] flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
        <div className="mr-auto min-w-0">
          <h1 className="text-xl leading-tight font-bold tracking-tight text-fg">Map</h1>
          <p className="truncate text-xs text-fg-muted" aria-live="polite">
            {summary}
          </p>
        </div>
        <Button
          ref={filtersButton}
          variant={panelOpen ? 'primary' : 'secondary'}
          size="sm"
          icon={<SlidersHorizontal aria-hidden />}
          aria-expanded={panelOpen}
          aria-controls={panelId}
          onClick={() => setPanelOpen((v) => !v)}
        >
          Filters
          {activeFilters > 0 && (
            <span
              className={cn(
                'ml-0.5 min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 font-bold tabular-nums',
                panelOpen ? 'bg-white/25' : 'bg-primary-soft text-primary-text',
              )}
            >
              {activeFilters}
              <span className="sr-only"> active</span>
            </span>
          )}
        </Button>
        {can.canSelectOnMap && (
          <Button
            variant={selectMode ? 'primary' : 'secondary'}
            size="sm"
            icon={<SquareMousePointer aria-hidden />}
            aria-pressed={selectMode}
            title={selectMode ? 'Stop selecting stores' : 'Select stores on the map to assign them'}
            onClick={toggleSelectMode}
          >
            Select
          </Button>
        )}
        <ColorByMenu value={mode} onChange={setMode} defaultMode={settings.defaultMapColorize} compact />
        <DropdownMenu
          label="Map layers"
          placement="bottom-end"
          minWidth={260}
          items={[
            { type: 'label', label: 'Overlays' },
            {
              id: 'territories',
              label: 'Territories',
              description: `${formatGrouped(polygons.length)} drawn`,
              checked: layers.territories,
              keepOpen: true,
              onSelect: () => setLayers({ ...layers, territories: !layers.territories }),
            },
            ...(can.canSeeRepLocations
              ? [
                  {
                    id: 'reps',
                    label: 'Team last locations',
                    description: `${formatGrouped(markers.length)} ${markers.length === 1 ? 'rep' : 'reps'} with a known position`,
                    checked: layers.reps,
                    keepOpen: true,
                    onSelect: () => setLayers({ ...layers, reps: !layers.reps }),
                  },
                ]
              : []),
          ]}
          trigger={
            <Button variant="secondary" size="sm" icon={<Layers aria-hidden />}>
              <span className="max-sm:sr-only">Layers</span>
            </Button>
          }
        />
        <IconButton label="Fit the map to the stores" icon={<Maximize />} size="sm" variant="secondary" onClick={() => mapRef.current?.fitToData()} />
        {can.canCreateRecords && (
          <Button variant="secondary" size="sm" icon={<Upload aria-hidden />} title="Import stores from a CSV file" onClick={() => setImportOpen(true)}>
            <span className="max-sm:sr-only">Import</span>
          </Button>
        )}
        <Link
          to={listHref}
          className="inline-flex h-8 items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-semibold text-link hover:bg-surface-3/70 focus-visible:outline-2 focus-visible:outline-focus [&_svg]:size-3.5"
        >
          <Rows3 aria-hidden />
          <span className="max-sm:sr-only">Open as list</span>
        </Link>
      </div>

      <div className="relative flex min-h-0 flex-1">
        {panelOpen && (
          <aside
            id={panelId}
            aria-label="Filters and stores"
            onKeyDown={(e) => {
              // The narrow-screen drawer covers the map: Escape closes it (and only it; the peek stays) and puts focus
              // back on the Filters button. Fields inside that use Escape themselves (search clears) prevent it first.
              if (e.key !== 'Escape' || isWide || e.defaultPrevented) return;
              e.preventDefault();
              setPanelOpen(false);
              filtersButton.current?.focus();
            }}
            className={cn(
              'z-20 flex w-[340px] max-w-[85vw] shrink-0 flex-col border-r border-line bg-surface',
              isWide ? 'relative' : 'absolute inset-y-0 left-0 shadow-pop animate-slide-in-left',
            )}
          >
            <Tabs
              value={tab}
              onValueChange={setTab}
              label="Side panel"
              className="flex min-h-0 flex-1 flex-col"
              listClassName="shrink-0 px-2"
              panelClassName="min-h-0 flex-1 overflow-y-auto gt-scroll pt-0!"
              items={[
                { value: 'filters', label: 'Filters', icon: <ListFilter aria-hidden />, ...(activeFilters > 0 ? { count: activeFilters } : {}) },
                { value: 'stores', label: 'Stores', count: stores.located.length },
              ]}
            >
              {(active) =>
                active === 'filters' ? (
                  <MapFilterPanel
                    filter={filter}
                    onChange={setFilter}
                    resultCount={stores.matching.length}
                    owners={owners}
                    showOwners={can.isManager}
                    pipelines={pipelines.values}
                    visitResults={orderedResults}
                    territories={territoryOptions}
                    tags={tags}
                  />
                ) : (
                  <MapStoreList
                    stores={stores.located}
                    colors={pinColors}
                    selectedId={shownPeekId}
                    onSelect={selectStore}
                    {...(selecting ? { selection, onSelectionChange: onListSelectionChange } : {})}
                  />
                )
              }
            </Tabs>
          </aside>
        )}

        <div className="relative min-w-0 flex-1">
          <MapCanvas
            ref={mapRef}
            ariaLabel={`Map of ${formatGrouped(points.length)} ${points.length === 1 ? 'store' : 'stores'}`}
            points={points}
            polygons={layers.territories ? polygons : undefined}
            markers={can.canSeeRepLocations && layers.reps ? markers : undefined}
            selectedPointId={shownPeekId}
            highlightedPointIds={selecting ? selection : null}
            boxSelect={selecting ? (boxTool ? 'drag' : 'shift') : 'off'}
            onBoxSelect={onBoxSelect}
            onPointClick={onPointClick}
            onMapReady={onMapReady}
          >
            <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-16">
              <MapBanner
                totalVisible={stores.totalVisible}
                matching={stores.matching.length}
                unlocated={stores.unlocatedCount}
                filterActive={stores.filterActive}
                onReset={resetFilter}
              />
            </div>

            {stores.totalVisible === 0 ? (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-6">
                <div className="pointer-events-auto max-w-sm rounded-card border border-line bg-surface/95 shadow-raised backdrop-blur">
                  {can.canCreateRecords ? (
                    <NoStoresYet
                      compact
                      message={NO_STORES_IMPORT_TEXT}
                      action={
                        <Button variant="primary" icon={<Upload aria-hidden />} onClick={() => setImportOpen(true)}>
                          Import stores
                        </Button>
                      }
                    />
                  ) : (
                    <NoStoresYet compact />
                  )}
                </div>
              </div>
            ) : (
              // Legend bottom left; in select mode the selection bar sits under it, centered, so they never overlap.
              <div className="pointer-events-none absolute inset-x-3 bottom-6 z-10 flex flex-col items-start gap-2">
                <InViewLegend located={stores.located} mode={mode} lookups={lk} viewport={viewport} filterActive={stores.filterActive} />
                {selecting && (
                  <MapSelectionBar
                    className="self-center"
                    count={selected.length}
                    shownCount={stores.located.length}
                    boxTool={boxTool}
                    onBoxToolChange={setBoxTool}
                    onSelectAll={selectAllShown}
                    onClear={clearSelection}
                    boxToolRef={boxToolButton}
                    {...(can.canAssign ? { onAssign: () => setAssignOpen(true) } : {})}
                  />
                )}
              </div>
            )}

            {peekItem && (
              <div className="pointer-events-none absolute right-3 bottom-9 left-3 z-10 flex justify-end sm:left-auto">
                <PeekCard item={peekItem} onClose={closePeek} />
              </div>
            )}
          </MapCanvas>
        </div>
      </div>

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        stores={selected}
        people={owners}
        users={lk.users}
        onAssigned={onAssigned}
      />
      {can.canCreateRecords && <ImportStoresDialog open={importOpen} onClose={() => setImportOpen(false)} />}
    </div>
  );
}

/** The query string for the Stores list link ("" when nothing is filtered): the shared `filterToQuery` format. */
function filterQueryString(filter: MapFilter): string {
  const q = filterToQuery(filter).toString();
  return q === '' ? '' : `?${q}`;
}

/**
 * The legend over the pins in the visible region, as iOS counts them (MapCanvasView: `legend(for:records:
 * set.visible)`). It subscribes to the viewport itself so a pan re-renders only the legend.
 */
function InViewLegend({
  located,
  mode,
  lookups,
  viewport,
  filterActive,
}: {
  located: readonly RecordPayload[];
  mode: MapColorize;
  lookups: RecordLookups;
  viewport: ViewportStore;
  filterActive: boolean;
}) {
  const bounds = useSyncExternalStore(viewport.subscribe, viewport.get, viewport.get);
  const entries = useMemo(() => mapLegend(storesInView(located, bounds), mode, lookups), [located, bounds, mode, lookups]);
  return <MapLegend mode={mode} entries={entries} emptyText={legendEmptyText(located.length, filterActive)} />;
}

/** Holds the StoreRow lookups (which tick every minute for presence) so only the peek re-renders on the tick. */
function PeekCard({ item, onClose }: { item: DataItem<RecordPayload>; onClose: () => void }) {
  const lookups = useStoreRowLookups();
  return <StorePeek item={item} lookups={lookups} onClose={onClose} />;
}

/**
 * The banner at the top of the map (MapCanvasView.banner): "12 of 240 stores match your filters" with Reset, and how
 * many matching stores are not drawn because they have no location (iOS silently leaves them out).
 */
function MapBanner({
  totalVisible,
  matching,
  unlocated,
  filterActive,
  onReset,
}: {
  totalVisible: number;
  matching: number;
  unlocated: number;
  filterActive: boolean;
  onReset: () => void;
}) {
  if (totalVisible === 0 || (!filterActive && unlocated === 0)) return null;
  const parts: string[] = [];
  if (filterActive) {
    parts.push(
      matching === 0
        ? 'No stores match your filters'
        : `${formatGrouped(matching)} of ${formatGrouped(totalVisible)} stores match your filters`,
    );
  }
  if (unlocated > 0) parts.push(`${formatGrouped(unlocated)} without a location ${unlocated === 1 ? 'is' : 'are'} not on the map`);
  return (
    <div
      className={cn(
        'pointer-events-auto flex max-w-full items-center gap-2 rounded-full border border-line bg-surface/95 py-1.5 pr-1.5 pl-3 text-xs font-medium text-fg shadow-raised backdrop-blur',
        !filterActive && 'pr-3',
      )}
    >
      {filterActive && <ListFilter aria-hidden className="size-3.5 shrink-0 text-red" strokeWidth={2.5} />}
      <span className="min-w-0 truncate">{parts.join(' · ')}</span>
      {filterActive && (
        <button
          type="button"
          onClick={onReset}
          className="h-6 shrink-0 rounded-full px-2.5 text-xs font-semibold text-link hover:bg-surface-3 focus-visible:outline-2 focus-visible:outline-focus"
        >
          Reset
        </button>
      )}
    </div>
  );
}
